'use client';

import * as React from 'react';
import {
  FiCalendar as _FiCalendar,
  FiClock as _FiClock,
  FiX as _FiX,
  FiChevronLeft as _FiChevronLeft,
  FiChevronRight as _FiChevronRight,
  FiPlus as _FiPlus,
  FiSave,
  FiCheck,
  FiRefreshCw,
  FiTrendingUp,
} from 'react-icons/fi';

import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Separator,
  Input,
  IconButton as _IconButton,
  Dialog,
  Badge,
  Tabs,
  useResponsiveValue,
} from '../../index';
import {
  DateRange,
  DateRangePreset,
  SavedRange,
  formatDateRange,
  createPresetRanges,
  validateDateRange,
  getComparisonPeriod,
  calculatePercentageChange,
} from '../../utils/date-utils';

import { QuickPresets } from './QuickPresets';

export interface DatePickerDialogProps {
  /** Current selected range */
  value?: DateRange;
  /** Called when range changes */
  onChange?: (range: DateRange) => void;
  /** Trigger element */
  trigger?: React.ReactNode;
  /** Preset ranges to show */
  presets?: DateRangePreset[];
  /** Minimum selectable date */
  minDate?: Date | string;
  /** Maximum selectable date */
  maxDate?: Date | string;
  /** Enable time selection */
  withTime?: boolean;
  /** Enable comparison mode */
  withCompare?: boolean;
  /** Timezone for date display */
  timezone?: string;
  /** Initial view mode */
  defaultView?: 'presets' | 'calendar' | 'custom';
  /** Saved ranges */
  savedRanges?: SavedRange[];
  /** Called when saved ranges change */
  onSavedRangesChange?: (ranges: SavedRange[]) => void;
  /** Localization */
  locale?: string;
  /** Custom styling */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether dialog is open */
  open?: boolean;
  /** Called when dialog opens/closes */
  onOpenChange?: (open: boolean) => void;
}

/**
 * DatePickerDialog - A comprehensive date range picker component
 * Provides multiple ways to select date ranges with preset options,
 * custom calendar selection, and saved ranges functionality
 */
export const DatePickerDialog: React.FC<DatePickerDialogProps> = ({
  value = { startDate: null, endDate: null },
  onChange,
  trigger,
  presets,
  minDate,
  maxDate,
  withTime = false,
  withCompare = false,
  timezone = 'America/New_York',
  defaultView = 'presets',
  savedRanges = [],
  onSavedRangesChange,
  locale = 'en-US',
  size = 'md',
  open: controlledOpen,
  onOpenChange,
}) => {
  // State management
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [selectedView, setSelectedView] = React.useState(defaultView);
  const [tempRange, setTempRange] = React.useState<DateRange>(value);
  const [comparisonRange, setComparisonRange] = React.useState<DateRange | null>(null);
  const [isSavingRange, setIsSavingRange] = React.useState(false);
  const [saveRangeName, setSaveRangeName] = React.useState('');
  const [saveRangeEmoji, setSaveRangeEmoji] = React.useState('📅');

  // Dialog open state (controlled or uncontrolled)
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  // Responsive size adjustments
  const dialogSize = useResponsiveValue({
    base: 'full',
    md: size === 'lg' ? 'lg' : size === 'sm' ? 'md' : 'md',
  } as const);

  const isTurkish = locale.startsWith('tr');

  // Default presets
  const defaultPresets = React.useMemo(
    () => presets || createPresetRanges(locale),
    [presets, locale]
  );

  // Sync temp range when value changes (with deep comparison to prevent infinite loops)
  // Defensive: Handle both Date objects and date strings
  const valueStartTime =
    value.startDate instanceof Date
      ? value.startDate.getTime()
      : value.startDate
        ? new Date(value.startDate).getTime()
        : null;
  const valueEndTime =
    value.endDate instanceof Date
      ? value.endDate.getTime()
      : value.endDate
        ? new Date(value.endDate).getTime()
        : null;

  React.useEffect(() => {
    // Update temp range when external value prop changes
    setTempRange(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on primitive timestamps to avoid infinite re-renders from object reference changes
  }, [valueStartTime, valueEndTime]);

  // Handle range selection
  const handleSelectRange = (range: DateRange) => {
    setTempRange(range);

    // Update comparison if enabled
    if (withCompare && range.startDate && range.endDate) {
      setComparisonRange(getComparisonPeriod(range));
    }
  };

  // Apply selected range
  const handleApply = () => {
    const validation = validateDateRange(tempRange);
    if (!validation.isValid) {
      // Show error notification in real implementation
      return;
    }

    onChange?.(tempRange);
    setIsOpen(false);
  };

  // Reset to current value
  const handleReset = () => {
    setTempRange(value);
    setComparisonRange(null);
  };

  // Save current range
  const handleSaveRange = () => {
    if (!tempRange.startDate || !tempRange.endDate || !saveRangeName.trim()) {
      return;
    }

    const newSavedRange: SavedRange = {
      id: `saved-${Date.now()}`,
      name: saveRangeName.trim(),
      emoji: saveRangeEmoji,
      range: { ...tempRange },
      createdAt: new Date(),
    };

    const updatedRanges = [...savedRanges, newSavedRange];
    onSavedRangesChange?.(updatedRanges);

    setIsSavingRange(false);
    setSaveRangeName('');
    setSaveRangeEmoji('📅');
  };

  // Delete saved range
  const handleDeleteSavedRange = (id: string) => {
    const updatedRanges = savedRanges.filter((range) => range.id !== id);
    onSavedRangesChange?.(updatedRanges);
  };

  // Format display text
  const displayText = React.useMemo(() => {
    return formatDateRange(tempRange, {
      locale,
      timezone,
      includeTime: withTime,
      separator: ' - ',
    });
  }, [tempRange, locale, timezone, withTime]);

  // Calculate comparison metrics if enabled
  const comparisonMetrics = React.useMemo(() => {
    if (!withCompare || !comparisonRange || !value.startDate || !value.endDate) {
      return null;
    }

    // This would normally use actual data from the application
    // For now, we'll use placeholder calculations
    const currentValue = 100; // placeholder
    const previousValue = 85; // placeholder

    return {
      current: currentValue,
      previous: previousValue,
      ...calculatePercentageChange(currentValue, previousValue),
    };
  }, [withCompare, comparisonRange, value]);

  const viewTabs = [
    { id: 'presets', label: isTurkish ? 'Hızlı Seçim' : 'Quick Select' },
    { id: 'calendar', label: isTurkish ? 'Takvim' : 'Calendar' },
    { id: 'custom', label: isTurkish ? 'Özel' : 'Custom' },
  ];

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details: { open: boolean }) => setIsOpen(details.open)}
      size={dialogSize}
      placement="center"
      preventScroll
      closeOnInteractOutside
      closeOnEscape
      trapFocus
      lazyMount
      unmountOnExit
    >
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}

      <Dialog.Backdrop />
      {/* RULE 15: Proper Chakra UI v3 pattern with type assertion for Dialog.Positioner */}
      <Dialog.Positioner {...({} as React.ComponentProps<typeof Dialog.Positioner>)}>
        <Dialog.Content>
          <Dialog.Header>
            <HStack justifyContent="space-between" align="center">
              <Text fontSize="lg" fontWeight="semibold">
                {isTurkish ? 'Tarih Aralığı Seç' : 'Select Date Range'}
              </Text>
              <Dialog.CloseTrigger />
            </HStack>
          </Dialog.Header>

          <Dialog.Body>
            <VStack gap={4} align="stretch">
              {/* Current Selection Display */}
              <Box padding={4} borderRadius="md" backgroundColor="bg.subtle">
                <VStack gap={2} align="stretch">
                  <HStack justifyContent="space-between" align="center">
                    <Text fontSize="sm" color="fg.muted">
                      {isTurkish ? 'Seçili Aralık' : 'Selected Range'}
                    </Text>
                    <HStack gap={2}>
                      {withCompare && comparisonMetrics && (
                        <Badge
                          size="sm"
                          colorPalette={comparisonMetrics.isPositive ? 'green' : 'red'}
                        >
                          <FiTrendingUp size={12} />
                          {comparisonMetrics.isPositive ? '+' : ''}
                          {comparisonMetrics.percentage}%
                        </Badge>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={handleReset}
                        disabled={
                          !tempRange.startDate &&
                          !tempRange.endDate &&
                          !value.startDate &&
                          !value.endDate
                        }
                      >
                        <FiRefreshCw size={14} />
                      </Button>
                    </HStack>
                  </HStack>
                  <Text fontSize="md" fontWeight="medium">
                    {displayText || (isTurkish ? 'Tarih seçilmedi' : 'No date selected')}
                  </Text>
                  {comparisonRange && (
                    <Text fontSize="xs" color="fg.muted">
                      {isTurkish ? 'Karşılaştırma: ' : 'Comparison: '}
                      {formatDateRange(comparisonRange, { locale, timezone })}
                    </Text>
                  )}
                </VStack>
              </Box>

              {/* View Tabs */}
              <Tabs.Root
                value={selectedView}
                onValueChange={(value: string) =>
                  setSelectedView(value as 'presets' | 'calendar' | 'custom')
                }
              >
                <Tabs.List>
                  {viewTabs.map((tab) => (
                    <Tabs.Trigger key={tab.id} value={tab.id}>
                      {tab.label}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>

                <Tabs.ContentGroup>
                  {/* Quick Presets View */}
                  <Tabs.Content value="presets">
                    <QuickPresets
                      value={tempRange}
                      onSelect={handleSelectRange}
                      presets={defaultPresets}
                      savedRanges={savedRanges}
                      onDeleteSavedRange={handleDeleteSavedRange}
                      locale={locale}
                      timezone={timezone}
                    />
                  </Tabs.Content>

                  {/* Calendar View */}
                  <Tabs.Content value="calendar">
                    <Box padding={4}>
                      <Text textAlign="center" color="fg.muted">
                        {isTurkish
                          ? 'Takvim görünümü yakında gelecek'
                          : 'Calendar view coming soon'}
                      </Text>
                    </Box>
                  </Tabs.Content>

                  {/* Custom Range View */}
                  <Tabs.Content value="custom">
                    <VStack gap={4} align="stretch" padding={4}>
                      <Text fontSize="sm" color="fg.muted">
                        {isTurkish
                          ? 'Başlangıç ve bitiş tarihlerini manuel olarak girin'
                          : 'Manually enter start and end dates'}
                      </Text>

                      <HStack gap={3}>
                        <VStack gap={1} flex={1}>
                          <Text fontSize="xs" color="fg.muted">
                            {isTurkish ? 'Başlangıç' : 'Start'}
                          </Text>
                          <Input
                            type={withTime ? 'datetime-local' : 'date'}
                            value={
                              tempRange.startDate
                                ? (tempRange.startDate instanceof Date
                                    ? tempRange.startDate
                                    : new Date(tempRange.startDate)
                                  )
                                    .toISOString()
                                    .slice(0, withTime ? 16 : 10)
                                : ''
                            }
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const date = e.target.value ? new Date(e.target.value) : null;
                              setTempRange((prev) => ({
                                ...prev,
                                startDate: date,
                              }));
                            }}
                            min={minDate ? new Date(minDate).toISOString().slice(0, 10) : undefined}
                            max={maxDate ? new Date(maxDate).toISOString().slice(0, 10) : undefined}
                          />
                        </VStack>

                        <VStack gap={1} flex={1}>
                          <Text fontSize="xs" color="fg.muted">
                            {isTurkish ? 'Bitiş' : 'End'}
                          </Text>
                          <Input
                            type={withTime ? 'datetime-local' : 'date'}
                            value={
                              tempRange.endDate
                                ? (tempRange.endDate instanceof Date
                                    ? tempRange.endDate
                                    : new Date(tempRange.endDate)
                                  )
                                    .toISOString()
                                    .slice(0, withTime ? 16 : 10)
                                : ''
                            }
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const date = e.target.value ? new Date(e.target.value) : null;
                              setTempRange((prev) => ({
                                ...prev,
                                endDate: date,
                              }));
                            }}
                            min={
                              tempRange.startDate
                                ? (tempRange.startDate instanceof Date
                                    ? tempRange.startDate
                                    : new Date(tempRange.startDate)
                                  )
                                    .toISOString()
                                    .slice(0, 10)
                                : minDate
                                  ? new Date(minDate).toISOString().slice(0, 10)
                                  : undefined
                            }
                            max={maxDate ? new Date(maxDate).toISOString().slice(0, 10) : undefined}
                          />
                        </VStack>
                      </HStack>

                      {/* Save Range */}
                      <Separator />

                      <VStack gap={3} align="stretch">
                        <HStack justifyContent="space-between" align="center">
                          <Text fontSize="sm" fontWeight="medium">
                            {isTurkish ? 'Aralığı Kaydet' : 'Save Range'}
                          </Text>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => setIsSavingRange(!isSavingRange)}
                            disabled={!tempRange.startDate || !tempRange.endDate}
                          >
                            <FiSave size={14} />
                            {isSavingRange
                              ? isTurkish
                                ? 'İptal'
                                : 'Cancel'
                              : isTurkish
                                ? 'Kaydet'
                                : 'Save'}
                          </Button>
                        </HStack>

                        {isSavingRange && (
                          <VStack gap={2} align="stretch">
                            <HStack gap={2}>
                              <Input
                                placeholder={
                                  isTurkish
                                    ? 'Aralık adı (ör: Son çeyrek)'
                                    : 'Range name (e.g., Last quarter)'
                                }
                                value={saveRangeName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  setSaveRangeName(e.target.value)
                                }
                                flex={1}
                              />
                              <Box
                                fontSize="xl"
                                cursor="pointer"
                                onClick={() => {
                                  const emojis = ['📅', '📊', '📈', '📉', '💼', '🎯', '⭐', '🚀'];
                                  const currentIndex = emojis.indexOf(saveRangeEmoji);
                                  const nextIndex = (currentIndex + 1) % emojis.length;
                                  setSaveRangeEmoji(emojis[nextIndex]);
                                }}
                              >
                                {saveRangeEmoji}
                              </Box>
                            </HStack>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={handleSaveRange}
                              disabled={!saveRangeName.trim()}
                            >
                              {isTurkish ? 'Kaydet' : 'Save'}
                            </Button>
                          </VStack>
                        )}
                      </VStack>
                    </VStack>
                  </Tabs.Content>
                </Tabs.ContentGroup>
              </Tabs.Root>
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <HStack gap={3} justifyContent="flex-end">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                {isTurkish ? 'İptal' : 'Cancel'}
              </Button>
              <Button
                variant="primary"
                onClick={handleApply}
                disabled={!tempRange.startDate && !tempRange.endDate}
              >
                <FiCheck size={16} />
                {isTurkish ? 'Uygula' : 'Apply'}
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
